<script lang="ts">
import type { BreadcrumbSegment } from "@/types/models"

/**
 * Breadcrumb showing an item's location in the container hierarchy.
 *
 * Each segment is an Inertia page visit to the container's show page.
 * Used on item show and container show pages. Renders nothing when
 * there are no ancestors.
 *
 * Distinct from `ContainerBreadcrumb` which manages client-side scope
 * within the containers index page.
 *
 * @example
 * ```vue
 * <LocationBreadcrumb :ancestors="item.ancestors" />
 * ```
 */
export interface LocationBreadcrumbProps {
  /** Ordered ancestor list from root to immediate parent. */
  ancestors: BreadcrumbSegment[]
}
</script>

<script setup lang="ts">
import { computed } from "vue"

import { itemPath } from "@/routes"

const props = defineProps<LocationBreadcrumbProps>()

const items = computed(() => props.ancestors.map((a) => ({ label: a.name, to: itemPath(a.id) })))
</script>

<template>
  <UBreadcrumb v-if="items.length" :items="items" />
</template>
